import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/permissions/permissions.js
import { authService } from '@/services/auth';
import { privilegesService } from '@/services/permissions/privilegesService';

/**
 * Permission types mapped to specific actions
 * These define all available permissions in the application
 * @type {Object.<string, string>}
 */
export const PERMISSIONS = {
  // User management permissions
  'user:view': 'View user details',
  'user:list': 'List all users in project',
  'user:create': 'Create new users',
  'user:delete': 'Delete existing users',
  'user:update': 'Update user details',
  'user:update:role': 'Change user roles',

  // Company profile permissions
  'profile:view': 'View company profile',
  'profile:edit': 'Edit company profile',

  // Carbon accounting permissions
  'carbon:view': 'View carbon data',
  'carbon:manage': 'Manage carbon data',

  // Module access permissions
  'module:carbon-accounting': 'Access carbon accounting module',
};

/**
 * Map of module access levels from APP_GET_PRIVILEGES to specific permissions
 * @type {Object.<string, Object.<string, string[]>>}
 */
const MODULE_PERMISSIONS = {
  AppMode: {
    GUIDED: [
      'user:view', 'user:list',
      'profile:view',
      'carbon:view'
    ],
    OPEN: [
      'user:view', 'user:list',
      'profile:view',
      'carbon:view', 'carbon:manage'
    ]
  },
  CarbonAccounting: {
    'GRANTED_FREE': [
      'carbon:view', 'carbon:manage',
      'module:carbon-accounting'
    ],
    'GRANTED_ITEMS': [
      'carbon:view', 'carbon:manage',
      'module:carbon-accounting'
    ]
  }
};

/**
 * Map of user roles to specific permissions
 * @type {Object.<string, string[]>}
 */
const ROLE_PERMISSIONS = {
  Admin: [
    'user:view', 'user:list', 'user:create', 'user:delete', 'user:update', 'user:update:role',
    'profile:view',
    'carbon:view', 'carbon:manage'
  ],
  User: [
    'user:view', 'user:list',
    'profile:view',
    'carbon:view'
  ]
};

/**
 * Permission service that centralizes all access control decisions
 * 
 * Provides a unified interface for checking user permissions based on:
 * - User roles (Admin, User)
 * - Module access levels (GUIDED, OPEN)
 * - Special permissions (e.g., main user)
 * 
 * Features:
 * - Permission caching for performance
 * - Automatic refresh of permissions data
 * - Fallback mechanisms for page reloads
 * - Background data loading to prevent UI blocking
 */
class PermissionsService {
  /**
   * Initialize the permission service
   */
  constructor() {
    this.initialized = false;
    this.permissionsCache = null;
    this.lastRefresh = 0;
    this.users = [];
    this.userDataLoaded = false;
    this.currentUserRole = null;
    this.currentUserEmail = null;
  }

  /**
   * Initialize permissions service
   * Loads users data and privileges, and calculates permissions
   * 
   * @returns {Promise<boolean>} Success status
   */
  async initialize() {
    if (this.initialized) {
      return true;
    }

    try {
      const isPageReload = typeof window !== 'undefined' &&
        window.performance?.navigation?.type === 1;

      const projectId = this._getReliableProjectId();
      const authState = authService.getAuthState();

      if (!authState?.authenticated && projectId) {
        if (isPageReload) {
          this.currentUserEmail = authState?.user?.email?.toLowerCase();
          this.permissionsCache = this.calculatePermissions();
          this.initialized = true;
          this._loadDataInBackground(projectId);
          return true;
        }
      } else if (!authState?.authenticated && !projectId) {
        this.initialized = true;
        return false;
      }

      this.currentUserEmail = authState?.user?.email?.toLowerCase();

      // Try to get users from appStore first (avoid duplicate API call)
      if (typeof window !== 'undefined' && window.appStore?.state?.data?.users?.list?.length > 0) {
        this.users = window.appStore.state.data.users.list;
        this.userDataLoaded = true;
        this.identifyUserRole();
        if (process.env.NODE_ENV === 'development') {
          console.log('[Permissions] Using users from appStore (no API call)');
        }
      } else {
        // Load users from API only if appStore doesn't have them
        const usersLoaded = await this.loadUsers();
        if (usersLoaded) {
          this.identifyUserRole();
        }
      }

      // Get privileges (already fetched by privilegesService)
      await privilegesService.getPrivileges();

      // Calculate permissions based on role and privileges
      this.permissionsCache = this.calculatePermissions();
      this.initialized = true;

      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to initialize permissions service:', error);
      }

      if (typeof window !== 'undefined' && window.performance?.navigation?.type === 1) {
        this.permissionsCache = this.calculatePermissions();
        this.initialized = true;
        return true;
      }

      this.initialized = true;
      return false;
    }
  }

  /**
   * Load data in background without blocking UI
   * 
   * @private
   * @param {string} projectId - Project ID
   */
  async _loadDataInBackground(_projectId) {
    setTimeout(async () => {
      try {
        await this.loadUsers();
        // Don't force — privileges were already fetched by the fast path
        // (getPrivileges(true) in store init). Forcing here caused a redundant
        // /privileges API call on every page reload (~300ms after the first).
        await privilegesService.getPrivileges();
        this.permissionsCache = this.calculatePermissions();
      } catch (error) {
        // Silent error in production
      }
    }, 100);
  }

  /**
   * Load users data from API with improved error handling
   * 
   * @returns {Promise<boolean>} Success status
   */
  async loadUsers() {
    try {
      const projectId = this._getReliableProjectId();
      if (!projectId) {
        return false;
      }

      const isPageReload = typeof window !== 'undefined' &&
        window.performance?.navigation?.type === 1;

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);
        const headers = this._getAuthHeaders();

        try {
          const response = await fetch(
            `${process.env.GATSBY_STRAPI_URL}/api/user-management/project/${projectId}/users`,
            {
              method: 'GET',
              headers,
              signal: controller.signal
            }
          );

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`Failed to load users: ${response.status}`);
          }

          const data = await response.json();
          this.users = data.data || [];
          this.userDataLoaded = true;

          if (typeof window !== 'undefined' && window.appStore?.state?.data) {
            window.appStore.state.data.users = { list: this.users };
          }

          return true;
        } catch (fetchError) {
          clearTimeout(timeoutId);
          throw fetchError;
        }
      } catch (fetchError) {
        if (isPageReload) {
          if (this.currentUserEmail) {
            this.currentUserRole = 'Admin';
          }
          return true;
        }
        throw fetchError;
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Get auth headers with better fallbacks
   * 
   * @private
   * @returns {Object} Headers object with Authorization if available
   */
  _getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json'
    };

    if (getStrapiToken()) {
      headers['Authorization'] = `Bearer ${getStrapiToken()}`;
      return headers;
    }

    const authToken = authService.getAuthState()?.token;
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
      return headers;
    }

    if (authService.keycloak?.token) {
      headers['Authorization'] = `Bearer ${authService.keycloak.token}`;
      return headers;
    }

    if (typeof sessionStorage !== 'undefined') {
      try {
        const tokenStateStr = sessionStorage.getItem('keycloakTokenState');
        if (tokenStateStr) {
          const tokenState = JSON.parse(tokenStateStr);
          if (tokenState?.token) {
            headers['Authorization'] = `Bearer ${tokenState.token}`;
            return headers;
          }
        }

        const appStateStr = sessionStorage.getItem('appState');
        if (appStateStr) {
          const appState = JSON.parse(appStateStr);
          if (appState?.authInfo?.token) {
            headers['Authorization'] = `Bearer ${appState.authInfo.token}`;
            return headers;
          }
        }
      } catch (e) {
        // Silent error in production
      }
    }

    return headers;
  }

  /**
   * Get the most reliable project ID by checking multiple sources
   * 
   * @private
   * @returns {string|null} Project ID or null if not available
   */
  _getReliableProjectId() {
    const projectId = authService.getProjectId();
    if (projectId) return projectId;

    if (typeof window !== 'undefined' && window.appStore?.getProjectId) {
      const storeProjectId = window.appStore.getProjectId();
      if (storeProjectId) return storeProjectId;
    }

    if (typeof sessionStorage !== 'undefined') {
      try {
        const cachedState = sessionStorage.getItem('appState');
        if (cachedState) {
          const parsedState = JSON.parse(cachedState);
          if (parsedState.projectId) return parsedState.projectId;
          if (parsedState.authInfo && parsedState.authInfo.projectId) {
            return parsedState.authInfo.projectId;
          }
        }
      } catch (e) {
        // Silent error in production
      }
    }

    return null;
  }

  /**
   * Identify current user's role from API data
   */
  identifyUserRole() {
    if (!this.currentUserEmail || !this.users.length) {
      return;
    }

    const currentUser = this.users.find(user =>
      user.email?.toLowerCase() === this.currentUserEmail
    );

    if (currentUser) {
      this.currentUserRole = currentUser.role;
    }
  }

  /**
   * Check if current user is an admin
   * 
   * @returns {boolean} True if user is admin
   */
  isAdmin() {
    return this.currentUserRole === 'Admin';
  }

  /**
   * Refresh permissions data from the server
   * 
   * @param {boolean} bypassCache - Force refresh data regardless of cache
   * @returns {Promise<boolean>} Success status
   */
  async refreshPermissions(bypassCache = false) {
    try {
      const projectId = this._getReliableProjectId();
      if (!projectId) {
        return false;
      }

      const isPageReload = typeof window !== 'undefined' &&
        window.performance?.navigation?.type === 1;

      try {
        const privileges = await privilegesService.getPrivileges(bypassCache);

        if (!privileges) {
          if (isPageReload) {
            this.permissionsCache = this.calculatePermissions();
            this.lastRefresh = Date.now();
            return true;
          }
          return false;
        }

        this.permissionsCache = this.calculatePermissions();
        this.lastRefresh = Date.now();

        return true;
      } catch (fetchError) {
        if (isPageReload &&
          (fetchError instanceof TypeError || fetchError.name === 'AbortError')) {
          this.permissionsCache = this.calculatePermissions();
          this.lastRefresh = Date.now();
          return true;
        }

        throw fetchError;
      }
    } catch (error) {
      if (typeof window !== 'undefined' && window.performance?.navigation?.type === 1) {
        this.permissionsCache = this.calculatePermissions();
        this.lastRefresh = Date.now();
        return true;
      }

      return false;
    }
  }

  /**
   * Calculate effective permissions based on user role and project privileges
   * 
   * @returns {Object} Effective permissions map
   */
  calculatePermissions() {
    if (!authService.getAuthState()?.authenticated) {
      return {};
    }

    // Start with role-based permissions
    const rolePermissions = ROLE_PERMISSIONS[this.currentUserRole] || ROLE_PERMISSIONS.User;
    const effectivePermissions = {
      ...rolePermissions.reduce((acc, perm) => ({ ...acc, [perm]: true }), {})
    };

    // Get privileges from privilegesService
    const privileges = privilegesService.projectPrivileges || privilegesService.getDefaultPrivileges();

    // Add module-based permissions
    if (privileges) {
      Object.entries(privileges).forEach(([module, access]) => {
        // Skip modules we don't have permission mappings for
        if (!MODULE_PERMISSIONS[module]) {
          return;
        }

        // Get access level for this module
        let accessLevel = null;

        if (typeof access === 'string') {
          // For simple string access levels (OPEN, GUIDED, GRANTED_FREE)
          accessLevel = access;
        }

        // Skip if unknown access level
        if (!accessLevel || !MODULE_PERMISSIONS[module][accessLevel]) {
          return;
        }

        // Add permissions for this module/access level
        const modulePerms = MODULE_PERMISSIONS[module][accessLevel];
        modulePerms.forEach(perm => {
          effectivePermissions[perm] = true;
        });
      });
    }

    return effectivePermissions;
  }

  /**
   * Check if user has a specific permission
   * 
   * @param {string} permission - Permission to check
   * @param {Object} context - Additional context for the check
   * @returns {boolean} True if user has permission
   */
  hasPermission(permission, context = {}) {
    if (!this.initialized) {
      if (typeof window !== 'undefined' && window.performance?.navigation?.type === 1) {
        const rolePermissions = ROLE_PERMISSIONS['Admin'];
        const tempPermissions = rolePermissions.reduce((acc, perm) => ({ ...acc, [perm]: true }), {});
        return !!tempPermissions[permission];
      }
      return false;
    }

    // Check permission cache
    const hasBasicPermission = !!this.permissionsCache?.[permission];

    // Special handling for user:delete permission
    if (permission === 'user:delete' && context.targetUser) {
      // Can't delete yourself
      if (context.targetUser.email?.toLowerCase() === this.currentUserEmail) {
        return false;
      }

      // Can't delete main user unless you are the main user
      if (context.targetUser.isMainUser && !this.isMainUser()) {
        return false;
      }
    }

    return hasBasicPermission;
  }

  /**
   * Check if current user is the main user
   * 
   * @returns {boolean} True if user is main user
   */
  isMainUser() {
    try {
      if (!this.currentUserEmail) return false;

      const isPageReload = typeof window !== 'undefined' &&
        window.performance?.navigation?.type === 1;

      // Check if user is marked as main user in API data first
      if (this.userDataLoaded) {
        const currentUser = this.users.find(user =>
          user.email?.toLowerCase() === this.currentUserEmail
        );

        if (currentUser && currentUser.isMainUser === true) {
          return true;
        }
      }

      // Check profile data as final path
      const profileData = window.appStore?.state?.data?.parsedProfile;
      if (profileData?.personal?.Email) {
        const mainUserEmail = profileData.personal.Email.toLowerCase();
        const isMainUser = this.currentUserEmail === mainUserEmail;
        return isMainUser;
      }

      // For page reloads without complete data, make a reasonable assumption
      if (isPageReload && !this.userDataLoaded) {
        if (this.currentUserEmail && this.currentUserRole === 'Admin') {
          return true;
        }
      }

      return false;
    } catch (error) {
      if (typeof window !== 'undefined' && window.performance?.navigation?.type === 1) {
        return this.currentUserRole === 'Admin';
      }

      return false;
    }
  }

  /**
   * Check if current user can edit company profile
   * 
   * @returns {boolean} True if user can edit profile
   */
  canEditProfile() {
    return this.isMainUser();
  }

  /**
   * Check if permissions need refresh based on time
   * 
   * @returns {boolean} True if refresh is needed
   */
  needsRefresh() {
    return Date.now() - this.lastRefresh > 5 * 60 * 1000 || privilegesService.needsRefresh();
  }
}

// Export singleton instance
export const permissionsService = new PermissionsService();